<template>
  <div>
    <BarChart
      ref="barRef"
      :height="250"
      :chartData="testData"
      :options="options"
      :plugins="plugins"
    />
  </div>
</template>

<script lang="ts">
import { BarChart, ExtractComponentData } from "vue-chart-3";
import { defineComponent, ref, computed, PropType } from "vue";
import { ChartOptions, ChartData } from "chart.js";

export default defineComponent({
  name: "bar-chart",
  components: { BarChart },
  props: {
    dataSet: {
      type: Object as PropType<{
        labels: string[];
        data: number[];
      }>,
      required: true,
    },
  },
  setup(props, context) {
    let lastEvent = "";
    const plugins = [
      {
        id: "drawLine",
        afterEvent(_: any, args: any) {
          lastEvent = args.event.type;
          if (
            [
              "click",
              "mouseout",
              "mouseup",
              "touchend",
              "touchcancel",
            ].includes(args.event.type)
          ) {
            context.emit("hoverEnd");
          }
        },
        afterDraw: (chart: any) => {
          if (
            [
              "click",
              "mouseout",
              "mouseup",
              "touchend",
              "touchcancel",
            ].includes(lastEvent) ||
            !chart.tooltip._active ||
            !chart.tooltip._active.length
          ) {
            return;
          }
          const activePoint = chart.tooltip._active[0],
            ctx = chart.ctx,
            x = activePoint.element.x,
            topY = chart.scales.y.top,
            bottomY = chart.scales.y.bottom;

          context.emit("hover", {
            value: props.dataSet.data[activePoint.index],
            label: props.dataSet.labels[activePoint.index],
          });
          // Render line on canvas
          ctx.save();
          ctx.beginPath();
          ctx.moveTo(x, bottomY);
          ctx.lineTo(x, topY);
          ctx.lineWidth = 2;
          ctx.strokeStyle = "#7B61FF";
          ctx.stroke();
          ctx.restore();
        },
      },
    ];
    const ctx = document.createElement("canvas").getContext("2d");
    let gradient: CanvasGradient | undefined = undefined;
    if (ctx) {
      gradient = ctx.createLinearGradient(0, 0, 0, 200);
      gradient.addColorStop(0, "rgba(123, 97, 255, 0.35)");
      gradient.addColorStop(1, "rgba(0, 0, 0, 0.02)");
    }

    const barRef = ref<ExtractComponentData<typeof BarChart>>();
    const options = ref<ChartOptions<"bar">>({
      responsive: true,
      interaction: {
        mode: "nearest",
        intersect: false,
      },
      events: [
        "mousemove",
        "mouseout",
        "click",
        "touchstart",
        "touchmove",
        "touchend",
      ],
      scales: {
        y: {
          grid: {
            color: "#32343B",
            lineWidth: 0.5,
            drawBorder: false,
          },
          afterFit: (s) => {
            s.width = 60;
          },
          ticks: {
            color: "#32343B",
            padding: 12,
            maxTicksLimit: 6,
            autoSkip: true,
            stepSize: 1,
            callback: (val) => {
              const amount = typeof val === "string" ? parseFloat(val) : val;
              return Math.round(amount);
            },
            precision: 1,
            font: {
              size: 11,
            },
          },
        },
        x: {
          grid: {
            display: false,
          },
          ticks: {
            color: "#32343B",
            font: {
              size: 11,
            },
          },
        },
      },
      elements: {
        point: {
          radius: 0,
        },
        line: {
          tension: 0.3,
        },
      },
      plugins: {
        legend: {
          display: false,
        },
        tooltip: {
          enabled: false,
          axis: "x",
        },
      },
    });
    const testData = computed<ChartData<"bar">>(() => ({
      labels: props.dataSet.labels,
      datasets: [
        {
          data: props.dataSet.data,
          backgroundColor: "#FF65D1",
          borderColor: "rgba(0,0,0,0)",
          borderWidth: 2,
          fill: true,
          borderRadius: 4,
          maxBarThickness: 16,
        },
      ],
    }));
    return { testData, barRef, options, plugins };
  },
});
</script>

<style scoped>
</style>